import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/students/health';

// Health Profile
export const fetchHealthProfile = createAsyncThunk(
  'health/fetchHealthProfile',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/health-profiles/?student=${studentId}`);
      return response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createHealthProfile = createAsyncThunk(
  'health/createHealthProfile',
  async ({ studentId, ...profileData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/health-profiles/`, {
        student: studentId,
        ...profileData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateHealthProfile = createAsyncThunk(
  'health/updateHealthProfile',
  async ({ id, ...profileData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/health-profiles/${id}/`, profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Allergies
export const fetchAllergies = createAsyncThunk(
  'health/fetchAllergies',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/allergies/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAllergy = createAsyncThunk(
  'health/createAllergy',
  async ({ studentId, ...allergyData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/allergies/`, {
        student: studentId,
        ...allergyData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAllergy = createAsyncThunk(
  'health/updateAllergy',
  async ({ id, ...allergyData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/allergies/${id}/`, allergyData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAllergy = createAsyncThunk(
  'health/deleteAllergy',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/allergies/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Medications
export const fetchMedications = createAsyncThunk(
  'health/fetchMedications',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/medications/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMedication = createAsyncThunk(
  'health/createMedication',
  async ({ studentId, ...medicationData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/medications/`, {
        student: studentId,
        ...medicationData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMedication = createAsyncThunk(
  'health/updateMedication',
  async ({ id, ...medicationData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/medications/${id}/`, medicationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMedication = createAsyncThunk(
  'health/deleteMedication',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/medications/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Health Incidents
export const fetchHealthIncidents = createAsyncThunk(
  'health/fetchHealthIncidents',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/health-incidents/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createHealthIncident = createAsyncThunk(
  'health/createHealthIncident',
  async ({ studentId, ...incidentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/health-incidents/`, {
        student: studentId,
        ...incidentData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateHealthIncident = createAsyncThunk(
  'health/updateHealthIncident',
  async ({ id, ...incidentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/health-incidents/${id}/`, incidentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteHealthIncident = createAsyncThunk(
  'health/deleteHealthIncident',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/health-incidents/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Medical Appointments
export const fetchMedicalAppointments = createAsyncThunk(
  'health/fetchMedicalAppointments',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/medical-appointments/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMedicalAppointment = createAsyncThunk(
  'health/createMedicalAppointment',
  async ({ studentId, ...appointmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/medical-appointments/`, {
        student: studentId,
        ...appointmentData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMedicalAppointment = createAsyncThunk(
  'health/updateMedicalAppointment',
  async ({ id, ...appointmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/medical-appointments/${id}/`, appointmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMedicalAppointment = createAsyncThunk(
  'health/deleteMedicalAppointment',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/medical-appointments/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Emergency Contacts
export const fetchEmergencyContacts = createAsyncThunk(
  'health/fetchEmergencyContacts',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/emergency-contacts/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createEmergencyContact = createAsyncThunk(
  'health/createEmergencyContact',
  async ({ studentId, ...contactData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/emergency-contacts/`, {
        student: studentId,
        ...contactData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEmergencyContact = createAsyncThunk(
  'health/updateEmergencyContact',
  async ({ id, ...contactData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/emergency-contacts/${id}/`, contactData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteEmergencyContact = createAsyncThunk(
  'health/deleteEmergencyContact',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/emergency-contacts/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Health Insurance
export const fetchHealthInsurances = createAsyncThunk(
  'health/fetchHealthInsurances',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/health-insurances/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createHealthInsurance = createAsyncThunk(
  'health/createHealthInsurance',
  async ({ studentId, ...insuranceData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/health-insurances/`, {
        student: studentId,
        ...insuranceData
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateHealthInsurance = createAsyncThunk(
  'health/updateHealthInsurance',
  async ({ id, ...insuranceData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/health-insurances/${id}/`, insuranceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteHealthInsurance = createAsyncThunk(
  'health/deleteHealthInsurance',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/health-insurances/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchExpiringInsurances = createAsyncThunk(
  'health/fetchExpiringInsurances',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/health-insurances/expiring_soon/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchExpiredInsurances = createAsyncThunk(
  'health/fetchExpiredInsurances',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/health-insurances/expired/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const healthSlice = createSlice({
  name: 'health',
  initialState: {
    healthProfile: null,
    allergies: [],
    medications: [],
    healthIncidents: [],
    medicalAppointments: [],
    emergencyContacts: [],
    healthInsurances: [],
    expiringInsurances: [],
    expiredInsurances: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearHealthData: (state) => {
      state.healthProfile = null;
      state.allergies = [];
      state.medications = [];
      state.healthIncidents = [];
      state.medicalAppointments = [];
      state.emergencyContacts = [];
      state.healthInsurances = [];
      state.expiringInsurances = [];
      state.expiredInsurances = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Health Profile
      .addCase(fetchHealthProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.healthProfile = action.payload;
      })
      .addCase(fetchHealthProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createHealthProfile.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createHealthProfile.fulfilled, (state, action) => {
        state.saving = false;
        state.healthProfile = action.payload;
      })
      .addCase(createHealthProfile.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateHealthProfile.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateHealthProfile.fulfilled, (state, action) => {
        state.saving = false;
        state.healthProfile = action.payload;
      })
      .addCase(updateHealthProfile.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      // Allergies
      .addCase(fetchAllergies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllergies.fulfilled, (state, action) => {
        state.loading = false;
        state.allergies = action.payload;
      })
      .addCase(fetchAllergies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAllergy.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createAllergy.fulfilled, (state, action) => {
        state.saving = false;
        state.allergies.push(action.payload);
      })
      .addCase(createAllergy.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateAllergy.fulfilled, (state, action) => {
        const index = state.allergies.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.allergies[index] = action.payload;
        }
      })
      .addCase(deleteAllergy.fulfilled, (state, action) => {
        state.allergies = state.allergies.filter((a) => a.id !== action.payload);
      })
      // Medications
      .addCase(fetchMedications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedications.fulfilled, (state, action) => {
        state.loading = false;
        state.medications = action.payload;
      })
      .addCase(fetchMedications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMedication.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createMedication.fulfilled, (state, action) => {
        state.saving = false;
        state.medications.push(action.payload);
      })
      .addCase(createMedication.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateMedication.fulfilled, (state, action) => {
        const index = state.medications.findIndex((m) => m.id === action.payload.id);
        if (index !== -1) {
          state.medications[index] = action.payload;
        }
      })
      .addCase(deleteMedication.fulfilled, (state, action) => {
        state.medications = state.medications.filter((m) => m.id !== action.payload);
      })
      // Health Incidents
      .addCase(fetchHealthIncidents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthIncidents.fulfilled, (state, action) => {
        state.loading = false;
        state.healthIncidents = action.payload;
      })
      .addCase(fetchHealthIncidents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createHealthIncident.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createHealthIncident.fulfilled, (state, action) => {
        state.saving = false;
        state.healthIncidents.push(action.payload);
      })
      .addCase(createHealthIncident.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateHealthIncident.fulfilled, (state, action) => {
        const index = state.healthIncidents.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.healthIncidents[index] = action.payload;
        }
      })
      .addCase(deleteHealthIncident.fulfilled, (state, action) => {
        state.healthIncidents = state.healthIncidents.filter((i) => i.id !== action.payload);
      })
      // Medical Appointments
      .addCase(fetchMedicalAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedicalAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.medicalAppointments = action.payload;
      })
      .addCase(fetchMedicalAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMedicalAppointment.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createMedicalAppointment.fulfilled, (state, action) => {
        state.saving = false;
        state.medicalAppointments.push(action.payload);
      })
      .addCase(createMedicalAppointment.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateMedicalAppointment.fulfilled, (state, action) => {
        const index = state.medicalAppointments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.medicalAppointments[index] = action.payload;
        }
      })
      .addCase(deleteMedicalAppointment.fulfilled, (state, action) => {
        state.medicalAppointments = state.medicalAppointments.filter((a) => a.id !== action.payload);
      })
      // Emergency Contacts
      .addCase(fetchEmergencyContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmergencyContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.emergencyContacts = action.payload;
      })
      .addCase(fetchEmergencyContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createEmergencyContact.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createEmergencyContact.fulfilled, (state, action) => {
        state.saving = false;
        state.emergencyContacts.push(action.payload);
      })
      .addCase(createEmergencyContact.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateEmergencyContact.fulfilled, (state, action) => {
        const index = state.emergencyContacts.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.emergencyContacts[index] = action.payload;
        }
      })
      .addCase(deleteEmergencyContact.fulfilled, (state, action) => {
        state.emergencyContacts = state.emergencyContacts.filter((c) => c.id !== action.payload);
      })
      // Health Insurance
      .addCase(fetchHealthInsurances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthInsurances.fulfilled, (state, action) => {
        state.loading = false;
        state.healthInsurances = action.payload;
      })
      .addCase(fetchHealthInsurances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createHealthInsurance.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createHealthInsurance.fulfilled, (state, action) => {
        state.saving = false;
        state.healthInsurances.push(action.payload);
      })
      .addCase(createHealthInsurance.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateHealthInsurance.fulfilled, (state, action) => {
        const index = state.healthInsurances.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.healthInsurances[index] = action.payload;
        }
      })
      .addCase(deleteHealthInsurance.fulfilled, (state, action) => {
        state.healthInsurances = state.healthInsurances.filter((i) => i.id !== action.payload);
      })
      .addCase(fetchExpiringInsurances.fulfilled, (state, action) => {
        state.expiringInsurances = action.payload;
      })
      .addCase(fetchExpiredInsurances.fulfilled, (state, action) => {
        state.expiredInsurances = action.payload;
      });
  },
});

export const { clearError, clearHealthData } = healthSlice.actions;
export default healthSlice.reducer;
